import clsx from "clsx";
import { type ComponentProps, useEffect, useRef } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Autoplay, EffectFade, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { useViewport } from "@widgets/device-info";

import { Image } from "@shared/ui/image";

import type { MerchVariantType } from "../../model/types";

import s from "./gallery.module.scss";

export type CompProps = ComponentProps<"div"> & {
  className?: string;
  data: MerchVariantType;
};

export const Gallery = (props: CompProps) => {
  const { className, data } = props;

  const { isDesktop } = useViewport();

  const $swiper = useRef<SwiperType | null>(null);

  useEffect(() => {
    if (isDesktop) {
      $swiper.current?.disable();
      return;
    }

    $swiper.current?.enable();
  }, [isDesktop]);

  return (
    <div className={clsx(s.root, className)}>
      {data?.images?.length > 0 && (
        <Swiper
          modules={[Autoplay, EffectFade, Pagination]}
          pagination={{
            el: `.${s.pagination}`,
            type: "bullets",
            clickable: true,
            bulletClass: `${s.bullet}`,
            bulletActiveClass: `${s.bulletActive}`,
          }}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          className={s.slider}
          wrapperClass={s.sliderWrapper}
          loop
          effect="fade"
          fadeEffect={{ crossFade: true }}
          onInit={(swiper) => {
            $swiper.current = swiper;
          }}
        >
          {data?.images?.map((image, index) => (
            <SwiperSlide key={`slide-event-${index + 1}`} className={s.slide}>
              <Image
                src={image?.url!}
                alt={image?.alternativeText || "merch image"}
                className={s.image}
                height="100%"
                objectFit="cover"
              />
            </SwiperSlide>
          ))}
          {data?.images?.length > 1 && <div className={s.pagination}></div>}
        </Swiper>
      )}
    </div>
  );
};

Gallery.displayName = "Gallery";
